import {zodResolver} from '@hookform/resolvers/zod';
import {UserGroup} from '@nursio-link/common/model/User';
import {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';

import {useConfirm} from '@/components/confirm-dialog/useConfirm';
import {Button} from '@/components/ui/button';
import type {DialogProps} from '@/components/ui/dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {Form} from '@/components/ui/form';
import {InputLabel} from '@/components/ui/input';
import {FormSkeleton} from '@/components/ui/skeleton';
import {Switch} from '@/components/ui/switch';
import {useToast} from '@/components/ui/use-toast';
import {useGroupAuthorize} from '@/hooks/useAuth';
import {graphqlClient} from '@/services/graphqlClient';
import {
  type Event,
  useDeleteEventMutation,
  useGetEventByIdQuery,
  useUpdateEventMutation,
} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';

import type {EventFormValues} from '../utils/eventsFormInputs';
import {
  eventFormDefaultValues,
  eventFormSchema,
  mapEventFormValuesToUpdateInput,
} from '../utils/eventsFormInputs';
import EventFormFields from './EventFormFields';

export type EditEventDialogProps = DialogProps;

const EditEventDialog = ({onOpenChange, ...props}: EditEventDialogProps) => {
  const {t} = useTranslation();
  const {confirm} = useConfirm();
  const isEmployee = useGroupAuthorize([
    UserGroup.Employees,
    UserGroup.EmployeesInventory,
  ]);
  const {toast} = useToast();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('editEvent') || '';

  const {data: eventData, isLoading: isEventLoading} = useGetEventByIdQuery(
    graphqlClient,
    {eventId: id},
    {enabled: !!id}
  );

  const deleteEvent = useDeleteEventMutation(graphqlClient, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['GetEventsByPairingId'],
      });
      queryClient.invalidateQueries({
        queryKey: ['GetEventsByDateRange'],
      });
    },
  });
  const updateEvent = useUpdateEventMutation(graphqlClient, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['GetEventsByPairingId'],
      });
      queryClient.invalidateQueries({
        queryKey: ['GetEventsByDateRange'],
      });
    },
  });

  const isDisabled = deleteEvent.isPending || updateEvent.isPending;

  const event = eventData?.getEventById as Event;

  const [isSharedToAuthorizedRelative, setIsSharedToAuthorizedRelative] =
    useState(false);

  const form = useForm<EventFormValues>({
    resolver: zodResolver(eventFormSchema),
    defaultValues: eventFormDefaultValues(isEmployee, event),
  });

  useEffect(() => {
    if (!event) return;
    form.reset(eventFormDefaultValues(isEmployee, event));
    setIsSharedToAuthorizedRelative(
      event.isSharedToAuthorizedRelative || false
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);

  const handleSubmit = async (values: EventFormValues) => {
    try {
      await updateEvent.mutateAsync({
        input: mapEventFormValuesToUpdateInput(
          values,
          event,
          isSharedToAuthorizedRelative
        ),
      });
      toast({
        title: t('Event updated'),
        variant: 'success',
      });
      onOpenChange?.(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async () => {
    try {
      await confirm({
        title: t('Delete event'),
        message: t('Are you sure you want to delete this event?'),
      });
      await deleteEvent.mutateAsync({id: event?.id});
      toast({
        title: t('Event deleted'),
        variant: 'success',
      });
      onOpenChange?.(true);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Form {...form}>
      <Dialog {...props} onOpenChange={onOpenChange}>
        <DialogContent asChild>
          <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
            <DialogHeader>
              <DialogTitle>{t('Event')}</DialogTitle>
            </DialogHeader>
            <DialogSection>
              {isEventLoading && <FormSkeleton />}
              {!isEventLoading && !event && <div>{t('Event not found')}</div>}
              {event && (
                <>
                  <EventFormFields form={form} />
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="share-event-switch"
                      checked={isSharedToAuthorizedRelative}
                      onCheckedChange={setIsSharedToAuthorizedRelative}
                    />
                    <InputLabel
                      htmlFor="share-event-switch"
                      className="text-xs text-marian-800"
                    >
                      {t('Share with contact person')}
                    </InputLabel>
                  </div>
                </>
              )}
            </DialogSection>
            <DialogFooter>
              {!!event && (
                <>
                  <Button
                    type="button"
                    disabled={isDisabled}
                    className="sm:mr-auto"
                    variant="warning"
                    onClick={() => handleDelete()}
                  >
                    {t('Delete event')}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => onOpenChange?.(false)}
                  >
                    {t('Close')}
                  </Button>
                  <Button variant="primary" type="submit" disabled={isDisabled}>
                    {t('Submit')}
                  </Button>
                </>
              )}
              {!event && (
                <Button
                  variant="primary"
                  type="button"
                  onClick={() => onOpenChange?.(false)}
                >
                  {t('Close')}
                </Button>
              )}
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Form>
  );
};

export default EditEventDialog;
